// SEPA-Lastschriftmandat erteilen.
//
// Der Text ist nicht frei formuliert. Ein Mandat ist nur wirksam, wenn der
// Zahler ihm in einer bestimmten Form zustimmt — mit Namen und
// Gläubiger-Identifikationsnummer des Einziehenden, der Ermächtigung an die
// eigene Bank und dem Hinweis auf die achtwöchige Erstattungsfrist. Fehlt
// eines davon, lässt sich das Mandat später angreifen, und zwar genau dann,
// wenn es darauf ankommt.
//
// Deshalb steht der Wortlaut vollständig da, bevor das Häkchen kommt — und
// nicht hinter einem Link, den niemand öffnet.
import { useCallback, useEffect, useState } from 'react';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Feld, Hinweis, Knopf, Spinner } from '../../../gemeinsam/komponenten/Basis';

type Angaben = {
  glaeubiger: { name: string; glaeubigerId: string | null };
  bereit: boolean;
  mandat: {
    holder: string; ibanMaskiert: string; bic: string | null;
    mandatsreferenz: string | null; signedAt: string;
  } | null;
};

/** IBAN-Prüfsumme nach ISO 13616 — damit ein Tippfehler sofort auffällt. */
function ibanPlausibel(roh: string): boolean {
  const iban = String(roh || '').replace(/[\s-]/g, '').toUpperCase();
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  const umgestellt = iban.slice(4) + iban.slice(0, 4);
  let rest = 0;
  for (const zeichen of umgestellt) {
    const wert = zeichen >= 'A' && zeichen <= 'Z' ? String(zeichen.charCodeAt(0) - 55) : zeichen;
    for (const ziffer of wert) rest = (rest * 10 + Number(ziffer)) % 97;
  }
  return rest === 1;
}

export default function SepaMandat({ beiErteilt, kompakt = false }: {
  beiErteilt?: () => void | Promise<void>;
  /** Ohne eigene Überschrift, zum Einsetzen in eine bestehende Karte. */
  kompakt?: boolean;
}) {
  const { t } = useSprache();
  const [angaben, setAngaben] = useState<Angaben | null>(null);
  const [inhaber, setInhaber] = useState('');
  const [iban, setIban] = useState('');
  const [bic, setBic] = useState('');
  const [zustimmung, setZustimmung] = useState(false);
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState(false);

  const laden = useCallback(async () => {
    try { setAngaben(await api<Angaben>('/mandant/sepa/angaben')); }
    catch (e) { setFehler(e instanceof ApiFehler ? e.message : t('mandat.ladefehler')); }
  }, [t]);

  useEffect(() => { void laden(); }, [laden]);

  const ibanOk = ibanPlausibel(iban);
  const bereit = inhaber.trim().length > 1 && ibanOk && zustimmung;

  async function erteilen(e: React.FormEvent) {
    e.preventDefault();
    if (!bereit) return;
    setSendet(true);
    setFehler(null);
    try {
      await api('/mandant/sepa', {
        methode: 'POST',
        daten: { holder: inhaber.trim(), iban: iban.replace(/\s/g, ''), bic: bic.replace(/\s/g, '') || undefined, zustimmung: true },
      });
      setFertig(true);
      await laden();
      await beiErteilt?.();
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : t('mandat.fehlgeschlagen'));
    } finally { setSendet(false); }
  }

  if (!angaben) return <div className="flex justify-center py-8"><Spinner /></div>;

  if (angaben.mandat && !fertig) {
    return (
      <div className="space-y-2 text-sm text-slate-700 dark:text-slate-300">
        <p className="font-medium text-slate-900 dark:text-slate-100">{angaben.mandat.holder}</p>
        <p className="font-mono text-slate-500 dark:text-slate-400">{angaben.mandat.ibanMaskiert}</p>
        {angaben.mandat.mandatsreferenz && (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            {t('mandat.referenz')}: <span className="font-mono">{angaben.mandat.mandatsreferenz}</span>
          </p>
        )}
        <Hinweis art="erfolg">{t('mandat.liegtVor')}</Hinweis>
      </div>
    );
  }

  if (fertig) return <Hinweis art="erfolg">{t('mandat.erteilt')}</Hinweis>;

  if (!angaben.bereit) {
    return <Hinweis art="warnung">{t('mandat.nochNichtBereit')}</Hinweis>;
  }

  const glaeubiger = angaben.glaeubiger.name;

  return (
    <form onSubmit={erteilen} className="space-y-4">
      {!kompakt && (
        <p className="text-sm text-slate-600 dark:text-slate-300">{t('mandat.einleitung')}</p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Feld label={t('mandat.kontoinhaber')} value={inhaber} required autoComplete="name"
                hinweis={t('mandat.kontoinhaberHinweis')}
                onChange={e => setInhaber(e.target.value)} />
        </div>
        <div className="sm:col-span-2">
          <Feld label="IBAN" value={iban} required inputMode="text" autoComplete="off"
                className="font-mono"
                fehler={iban.trim() && !ibanOk ? t('mandat.ibanFalsch') : undefined}
                onChange={e => setIban(e.target.value.toUpperCase())} />
        </div>
        <Feld label={`BIC (${t('allg.optional')})`} value={bic} autoComplete="off" className="font-mono"
              hinweis={t('mandat.bicHinweis')}
              onChange={e => setBic(e.target.value.toUpperCase())} />
      </div>

      {/* Der Wortlaut. Unverändert, weil genau er das Mandat trägt. */}
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300">
        <p className="mb-2 font-medium text-slate-900 dark:text-slate-100">{t('mandat.wortlautTitel')}</p>
        <p>
          Ich ermächtige {glaeubiger}, Zahlungen von meinem Konto mittels Lastschrift einzuziehen.
          Zugleich weise ich mein Kreditinstitut an, die von {glaeubiger} auf mein Konto gezogenen
          Lastschriften einzulösen.
        </p>
        <p className="mt-2">
          Hinweis: Ich kann innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die
          Erstattung des belasteten Betrages verlangen. Es gelten dabei die mit meinem
          Kreditinstitut vereinbarten Bedingungen.
        </p>
        <table className="mt-3 text-xs">
          <tbody>
            <tr>
              <td className="pe-4 align-top text-slate-500 dark:text-slate-400">{t('mandat.glaeubiger')}</td>
              <td className="font-medium">{glaeubiger}</td>
            </tr>
            <tr>
              <td className="pe-4 align-top text-slate-500 dark:text-slate-400">{t('mandat.glaeubigerId')}</td>
              <td className="font-mono">{angaben.glaeubiger.glaeubigerId}</td>
            </tr>
            <tr>
              <td className="pe-4 align-top text-slate-500 dark:text-slate-400">{t('mandat.referenz')}</td>
              <td>{t('mandat.referenzSpaeter')}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <label className="flex cursor-pointer items-start gap-3">
        <input type="checkbox" checked={zustimmung} onChange={e => setZustimmung(e.target.checked)}
               className="mt-0.5 h-4 w-4 rounded border-slate-300 text-marke-600" />
        <span className="text-sm text-slate-700 dark:text-slate-300">{t('mandat.zustimmung')}</span>
      </label>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}

      <Knopf type="submit" variante="primaer" className="w-full" laedt={sendet} disabled={!bereit}>
        {t('mandat.erteilen')}
      </Knopf>
    </form>
  );
}
